import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Col,
  ControlLabel,
  FormControl,
  FormGroup,
  Grid,
  Label,
  Modal,
  Row,
  Table,
} from "react-bootstrap";
import Select from "react-select";
import Card from "components/Card/Card";
import LoadingSpinner from "views/Components/loadingSpinner";
import "./rfqIntake.css";
import CreatableSelect from "react-select/lib/Creatable";
import {
  assignRfqMedication,
  createRfqDraftOrder,
  downloadRfqQuotationPdf,
  deleteRfqRequest,
  enrichRfqLines,
  getRfqMedicationCatalogue,
  getRfqMedicationStock,
  getRfqClientOrderDetail,
  getRfqClientOrderHistory,
  createRfqRequest,
  getRfqRequest,
  getRfqRequests,
  importRfqDocument,
  learnRfqLineMapping,
  parseRfqLines,
  searchRfqB2bClients,
  searchRfqMedications,
  searchRfqQuotationReps,
  selectRfqMatch,
  updateRfqRequest,
  updateRfqOutcome,
} from "api/rfqIntake/rfqIntake";

const outcomeOptions = [
  { value: "pending_outcome", label: "Pending" },
  { value: "won", label: "Won" },
  { value: "lost", label: "Lost" },
  { value: "partial", label: "Partial" },
];

const B2B_CLIENT_SEARCH_DEBOUNCE_MS = 300;
const B2B_CLIENT_SEARCH_MIN_LENGTH = 2;
const B2B_CLIENT_RESULT_LIMIT = 15;
const RECENT_RFQ_RESULT_LIMIT = 15;

const lossReasonOptions = [
  { value: "price_too_high", label: "Price too high" },
  { value: "competitor_brand", label: "Competitor brand" },
  { value: "stock_unavailable", label: "Stock unavailable" },
  { value: "customer_changed_mind", label: "Customer changed mind" },
  { value: "unclear", label: "Unclear" },
];

const emptyItem = {
  requested_name: "",
  requested_quantity: "",
  requested_unit: "",
  requested_description: "",
  medication_id: null,
  medication: null,
  customer_opportunity: null,
  pricing_summary: null,
  supplier_price_history: [],
  custom_medicine_name: "",
};

const styles = {
  sourceButton: {
    marginRight: 6,
    marginBottom: 6,
  },
  pasteBox: {
    minHeight: 178,
    resize: "vertical",
    fontFamily: "monospace",
    fontSize: 12,
  },
  metric: {
    border: "1px solid #e1e6ef",
    borderRadius: 4,
    padding: 12,
    marginBottom: 10,
    background: "#fbfcfe",
  },
  matchCard: {
    border: "1px solid #e0e4ea",
    borderRadius: 4,
    padding: 18,
    marginBottom: 18,
    background: "#fff",
  },
  tableInput: {
    minWidth: 90,
  },
  blockTitle: {
    display: "block",
    fontWeight: 600,
    marginBottom: 6,
  },
  outcomePanel: {
    border: "1px solid #e7ebf0",
    borderRadius: 4,
    padding: 12,
    background: "#fbfcff",
    marginTop: 12,
    height: "100%",
  },
  supplierCard: {
    border: "1px solid #e8edf2",
    borderRadius: 4,
    padding: 10,
    marginBottom: 8,
    background: "#fafcfe",
  },
  subtleText: {
    color: "#667085",
    fontSize: 12,
  },
  sectionSpacer: {
    marginTop: 16,
  },
  opportunityPanel: {
    border: "1px solid #dce8ff",
    borderLeft: "4px solid #1dc7ea",
    borderRadius: 4,
    padding: 12,
    background: "#f7fbff",
    marginTop: 12,
  },
  contextMetric: {
    border: "1px solid #e4e9f2",
    borderRadius: 8,
    padding: 12,
    background: "#ffffff",
    minHeight: 92,
  },
  pillMetric: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    borderRadius: 999,
    padding: "4px 10px",
    background: "#eef6ff",
    color: "#2b5fab",
    fontSize: 12,
    fontWeight: 600,
  },
};

const statusLabel = (status) => {
  const map = {
    draft_created: "success",
    matched: "success",
    ready: "success",
    review_ready: "info",
    outcome_recorded: "success",
    outcome_in_progress: "info",
    needs_review: "warning",
    missing_quantity: "warning",
    unmatched: "danger",
    low_margin: "danger",
    pending: "default",
    pending_outcome: "default",
    won: "success",
    lost: "danger",
    partial: "warning",
    repeat_winner: "success",
    previously_quoted_not_won: "warning",
    new_customer_item: "info",
    custom_quotation_item: "warning",
  };

  return map[status] || "default";
};

const getRequestItems = (request) => {
  if (!request || !request.items) return [];
  return Array.isArray(request.items) ? request.items : [];
};

const numberOrEmpty = (value) => {
  if (value === null || value === undefined || value === "") return "";
  return value;
};

const safeText = (value) => {
  if (value === null || value === undefined) return "";
  return String(value);
};

const normalizeSearchValue = (value) =>
  safeText(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const formatMoney = (value) => {
  if (value === null || value === undefined || value === "") return "-";

  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) return value;

  return numericValue.toFixed(2);
};

const parseMoneyValue = (value) => {
  if (value === null || value === undefined || value === "") return 0;
  const numericValue = Number(String(value).replace(/,/g, ""));
  return Number.isNaN(numericValue) ? 0 : numericValue;
};

const getRfqItemUnitPrice = (item) =>
  parseMoneyValue(
    item.quoted_unit_price ||
      (item.pricing_summary && item.pricing_summary.suggested_unit_price),
  );

const getRfqRequestTotal = (request) =>
  getRequestItems(request).reduce((total, item) => {
    const quantity = parseMoneyValue(item.requested_quantity) || 1;
    return total + quantity * getRfqItemUnitPrice(item);
  }, 0);

const formatPercent = (value) => {
  if (value === null || value === undefined || value === "") return "-";

  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) return value;

  return `${numericValue.toFixed(2)}%`;
};

const calculateMargin = (sellPrice, costPrice) => {
  const numericSellPrice = Number(sellPrice);
  const numericCostPrice = Number(costPrice);

  if (Number.isNaN(numericSellPrice) || Number.isNaN(numericCostPrice)) {
    return {
      margin_amount: null,
      margin_percentage: null,
    };
  }

  const marginAmount = numericSellPrice - numericCostPrice;

  return {
    margin_amount: marginAmount,
    margin_percentage: numericCostPrice > 0 ? (marginAmount / numericCostPrice) * 100 : null,
  };
};

const getMarginTone = (marginPercentage, marginAmount) => {
  const numericMargin = Number(marginPercentage);
  const numericMarginAmount = Number(marginAmount);

  if (Number.isNaN(numericMargin) && Number.isNaN(numericMarginAmount)) return "unknown";
  if (numericMarginAmount < 0 || numericMargin < 10) return "danger";
  if (numericMargin < 20) return "warning";
  return "success";
};

const getMarginLabel = (tone) => {
  const labels = {
    danger: "Low margin",
    warning: "Review margin",
    success: "Healthy margin",
  };

  return labels[tone] || "Margin pending";
};

const getMessageTone = (value) => {
  const text = safeText(value).toLowerCase();

  if (/could not|failed|error|no .*found|not found|unable/i.test(text)) return "danger";
  if (/select|check|missing|review|needed|required|try another|paste/i.test(text)) return "warning";
  if (/saved|downloaded|updated|created|imported|extracted|ready/i.test(text)) return "success";

  return "neutral";
};

const getDocumentImportMethodLabel = (method) => {
  const normalizedMethod = safeText(method).toLowerCase();

  if (normalizedMethod.includes("ai")) return "AI assisted reading";
  if (normalizedMethod === "pdf_text") return "PDF text reading";
  if (normalizedMethod === "pdf_ocr") return "PDF OCR reading";
  if (normalizedMethod === "image_ocr") return "image OCR reading";
  return "document reading";
};

const isAiDocumentImport = (method) => safeText(method).toLowerCase().includes("ai");

const isOcrDocumentImport = (method) => {
  const normalizedMethod = safeText(method).toLowerCase();
  return normalizedMethod.includes("ocr");
};

const getImportedItemsFromResponse = (response) => {
  const importData = response && response.data ? response.data : {};
  return {
    importData,
    items: Array.isArray(importData.items) ? importData.items : [],
  };
};

const hasWeakImportedText = (rawText, itemCount) => {
  const lines = safeText(rawText)
    .split(/\r\n|\r|\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  return lines.length >= 6 && itemCount <= Math.max(2, Math.floor(lines.length / 4));
};

const looksLikeWeakImportedItem = (item) => {
  const name = safeText(item && item.requested_name);
  const lettersOnly = name.replace(/[^A-Za-z]/g, "");
  const words = name.split(/\s+/).filter(Boolean);

  if (!name || lettersOnly.length < 4) return true;
  if (/^\d+\s+[a-z]\b/i.test(name)) return true;
  if (words.length > 1 && words.filter((word) => word.length <= 2).length >= words.length - 1) return true;
  if (safeText(item && item.confidence).toLowerCase() === "low") return true;

  return false;
};

const shouldRetryDocumentImportWithAi = (importData) => {
  const items = Array.isArray(importData.items) ? importData.items : [];
  if (!items.length || isAiDocumentImport(importData.extraction_method)) return false;
  if (!isOcrDocumentImport(importData.extraction_method)) return false;

  const weakItemCount = items.filter(looksLikeWeakImportedItem).length;
  if (weakItemCount >= Math.ceil(items.length / 2)) return true;
  if (hasWeakImportedText(importData.raw_text, items.length)) return true;

  return false;
};

const shouldBlockWeakDocumentImport = (importData) => {
  const items = Array.isArray(importData.items) ? importData.items : [];
  return Boolean(items.length && !isAiDocumentImport(importData.extraction_method) && shouldRetryDocumentImportWithAi(importData));
};

const isMissingOcrToolError = (error) => {
  const message = getApiErrorMessage(error, "");
  return /ocr needs|tesseract|ghostscript/i.test(message);
};

const formatDate = (value) => {
  if (!value) return "-";

  const dateValue = new Date(value);
  if (Number.isNaN(dateValue.getTime())) return value;

  return dateValue.toLocaleDateString();
};

const formatDateTime = (value) => {
  if (!value) return "-";

  const dateValue = new Date(value);
  if (Number.isNaN(dateValue.getTime())) return value;

  return dateValue.toLocaleString();
};

const getClientB2bId = (client) => {
  if (!client) return null;
  return client.b2bId || client.value || null;
};

const getClientLeadId = (client) => {
  if (!client) return null;
  return client.leadId || null;
};

const getClientDisplayName = (client) => {
  if (!client) return "";
  return client.label || client.b2bLabel || "";
};

const getClientHistoryName = (client) => {
  if (!client) return "";
  return client.b2bLabel || client.label || "";
};

const getClientQuotationName = (client) => {
  if (!client) return "";
  return client.b2bLabel || client.label || "";
};

const getClientWhatsappNumber = (client) => {
  if (!client) return "";
  const rawNumber = client.mobileNo || client.phone || "";
  return safeText(rawNumber).replace(/\D/g, "");
};

const getClientEmail = (client) => {
  if (!client) return "";
  return client.email || client.contactEmail || client.userEmail || "";
};

const makeQuotationRepOption = (user) => {
  if (!user) return null;
  const name = safeText(user.name);
  const mobile = safeText(user.mobile || user.mobile_no);
  const email = safeText(user.email);

  if (!name) return null;

  return {
    value: user.id,
    label: [name, mobile ? `(${mobile})` : ""].filter(Boolean).join(" "),
    name,
    mobile,
    email,
    designation: user.designation || user.job_title || "Sales Executive",
  };
};

const getRequestMetadata = (request) => {
  if (!request || !request.metadata) return {};
  if (typeof request.metadata === "string") {
    try {
      return JSON.parse(request.metadata) || {};
    } catch (error) {
      return {};
    }
  }
  return request.metadata;
};

const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const sanitizeFilenamePart = (value) =>
  safeText(value)
    .trim()
    .replace(/[^a-z0-9]+/gi, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase();

const getApiErrorMessage = (error, fallback) => {
  const data = error && error.response ? error.response.data : null;

  if (data) {
    if (typeof data === "string") {
      return data || fallback;
    }

    const validationDetails =
      data.data && typeof data.data === "object"
        ? Object.values(data.data)
            .reduce((messages, value) => messages.concat(Array.isArray(value) ? value : [value]), [])
            .filter(Boolean)
            .join(" ")
        : "";

    if (data.message && validationDetails) {
      return `${data.message} ${validationDetails}`;
    }

    if (data.message) {
      return data.message;
    }
  }

  return error && error.message ? error.message : fallback;
};

const scoreMedicationMatch = (medication, query) => {
  if (!medication) return -1;

  const normalizedQuery = normalizeSearchValue(query);
  const title = normalizeSearchValue(medication.title);
  const genericName = normalizeSearchValue(medication.generic_name);
  const itemCode = normalizeSearchValue(medication.item_code);
  const opportunityStatus =
    medication.customer_opportunity && medication.customer_opportunity.status
      ? medication.customer_opportunity.status
      : "";

  let score = 0;

  if (opportunityStatus === "repeat_winner") score += 1000;
  if (opportunityStatus === "previously_quoted_not_won") score += 400;
  if (
    medication.pricing_summary &&
    ["last_customer_order", "selected_lead_order"].includes(
      medication.pricing_summary.source,
    )
  ) {
    score += 250;
  }

  if (normalizedQuery && itemCode === normalizedQuery) score += 700;
  if (normalizedQuery && title === normalizedQuery) score += 650;
  if (normalizedQuery && genericName === normalizedQuery) score += 600;

  if (normalizedQuery && itemCode.startsWith(normalizedQuery)) score += 300;
  if (normalizedQuery && title.startsWith(normalizedQuery)) score += 260;
  if (normalizedQuery && genericName.startsWith(normalizedQuery)) score += 240;

  if (normalizedQuery && itemCode.includes(normalizedQuery)) score += 140;
  if (normalizedQuery && title.includes(normalizedQuery)) score += 120;
  if (normalizedQuery && genericName.includes(normalizedQuery)) score += 100;

  return score;
};

const getBestMedicationMatch = (medications, query) =>
  (medications || [])
    .filter(Boolean)
    .sort((left, right) => scoreMedicationMatch(right, query) - scoreMedicationMatch(left, query))[0] ||
  null;

const getApiList = (response) => {
  if (Array.isArray(response)) return response;
  if (response && Array.isArray(response.data)) return response.data;
  if (response && response.data && Array.isArray(response.data.data)) {
    return response.data.data;
  }
  return [];
};

const makeMedicationOption = (medication) => {
  if (!medication || !medication.id) return null;

  const title =
    safeText(medication.title) ||
    safeText(medication.generic_name) ||
    safeText(medication.item_code) ||
    "Untitled medicine";
  const itemCode = safeText(medication.item_code);

  return {
    value: medication.id,
    label: itemCode ? `${title} (${itemCode})` : title,
    payload: medication,
  };
};

const makeCustomMedicineOption = (name) => {
  const customName = safeText(name).trim();
  if (!customName) return null;

  return {
    value: `custom:${customName}`,
    label: customName,
    customName,
    isCustomMedicine: true,
  };
};

const uniqueMedicationOptions = (options) =>
  (options || [])
    .filter(Boolean)
    .filter(
      (option, index, allOptions) =>
        allOptions.findIndex((candidate) => candidate.value === option.value) === index,
    );

const getB2bOptionKey = (option) => {
  if (!option) return "";
  return option.leadId || option.b2bId || option.value || "";
};

const uniqueB2bOptions = (options) =>
  (options || [])
    .filter(Boolean)
    .filter((option, index, allOptions) => {
      const optionKey = getB2bOptionKey(option);
      return (
        allOptions.findIndex((candidate) => getB2bOptionKey(candidate) === optionKey) === index
      );
    });

const makeB2bClientOption = (lead) => {
  if (!lead) {
    return null;
  }

  const organizationId =
    lead.organization_id ||
    (lead.b2b && lead.b2b.id ? lead.b2b.id : null);
  const leadLabelParts = [safeText(lead.name)];
  if (safeText(lead.mobile_no)) {
    leadLabelParts.push(`(${safeText(lead.mobile_no)})`);
  }
  const leadLabel = leadLabelParts.filter(Boolean).join(" ").trim();
  const companyName =
    (lead.b2b && lead.b2b.company_name) ||
    safeText(lead.company_name) ||
    safeText(lead.subscriber_name) ||
    safeText(lead.name);

  if (!leadLabel && !companyName) {
    return null;
  }

  return {
    value: lead.id || organizationId,
    label: leadLabel || companyName,
    b2bId: organizationId,
    b2bLabel: companyName,
    leadId: lead.id || null,
    mobileNo: lead.mobile_no || "",
    email:
      lead.email ||
      lead.email_address ||
      lead.contact_email ||
      (lead.user && lead.user.email ? lead.user.email : ""),
  };
};

const filterB2bOptions = (options, search) => {
  const normalizedSearch = normalizeSearchValue(search);
  if (!normalizedSearch) return uniqueB2bOptions(options).slice(0, B2B_CLIENT_RESULT_LIMIT);

  return uniqueB2bOptions(options)
    .filter((option) => {
      const searchableText = normalizeSearchValue(
        [
          option.label,
          option.b2bLabel,
          option.mobileNo,
          option.email,
          option.value,
          option.leadId,
          option.b2bId,
        ].join(" "),
      );
      return searchableText.includes(normalizedSearch);
    })
    .slice(0, B2B_CLIENT_RESULT_LIMIT);
};

const getSelectedMedicationOption = (item, medicationOptions) => {
  if (item && item.custom_medicine_name) {
    return makeCustomMedicineOption(item.custom_medicine_name);
  }

  const itemMedicationId =
    item && item.medication_id
      ? item.medication_id
      : item && item.selected_match && item.selected_match.medication_id
        ? item.selected_match.medication_id
        : null;

  if (!itemMedicationId) return null;

  const existingOption = (medicationOptions || []).find(
    (option) => option && option.value === itemMedicationId,
  );
  if (existingOption) return existingOption;

  if (item.medication) {
    return makeMedicationOption(item.medication);
  }

  if (item.selected_match && item.selected_match.medication) {
    return makeMedicationOption(item.selected_match.medication);
  }

  return null;
};

const getSupplierHistory = (item) => {
  if (
    item &&
    item.selected_match &&
    item.selected_match.payload &&
    item.selected_match.payload.supplier_price_history
  ) {
    return item.selected_match.payload.supplier_price_history;
  }

  return [];
};

const getOutcomeSummary = (item) => {
  if (
    item &&
    item.selected_match &&
    item.selected_match.payload &&
    item.selected_match.payload.customer_outcome_summary
  ) {
    return item.selected_match.payload.customer_outcome_summary;
  }

  if (
    item &&
    item.selected_match &&
    item.selected_match.payload &&
    item.selected_match.payload.outcome_summary
  ) {
    return item.selected_match.payload.outcome_summary;
  }

  return null;
};

const getCustomerOpportunity = (item) => {
  if (item && item.customer_opportunity) {
    return item.customer_opportunity;
  }

  if (
    item &&
    item.selected_match &&
    item.selected_match.payload &&
    item.selected_match.payload.customer_opportunity
  ) {
    return item.selected_match.payload.customer_opportunity;
  }

  return null;
};

const getIntakeSupplierHistory = (item) =>
  item && Array.isArray(item.supplier_price_history) ? item.supplier_price_history : [];

const isOpportunityToWin = (opportunity) =>
  opportunity && opportunity.status && opportunity.status !== "repeat_winner";

const isRepeatWinner = (opportunity) =>
  opportunity && opportunity.status === "repeat_winner";

const getPricingSummary = (item) => {
  if (item && item.pricing_summary) {
    return item.pricing_summary;
  }

  if (item && item.medication) {
    const suggestedUnitPrice =
      item.medication.price || item.medication.unit_price || item.medication.selling_price || null;
    const purchaseCost =
      item.medication.buying_price || item.medication.unit_price || null;
    const marginAmount =
      suggestedUnitPrice !== null && purchaseCost !== null
        ? Number(suggestedUnitPrice) - Number(purchaseCost)
        : null;
    const marginPercentage =
      marginAmount !== null && Number(purchaseCost) > 0
        ? (marginAmount / Number(purchaseCost)) * 100
        : null;

    return suggestedUnitPrice !== null || purchaseCost !== null
      ? {
          purchase_cost: purchaseCost,
          suggested_unit_price: suggestedUnitPrice,
          margin_amount: marginAmount,
          margin_percentage: marginPercentage,
          source: "medicine_master",
        }
      : null;
  }

  if (item && item.selected_match) {
    return {
      purchase_cost: item.selected_match.purchase_cost,
      suggested_unit_price: item.selected_match.suggested_unit_price,
      margin_amount: item.selected_match.margin_amount,
      margin_percentage: item.selected_match.margin_percentage,
    };
  }

  return null;
};

const getEffectivePricingSummary = (item) => {
  const pricingSummary = getPricingSummary(item);
  if (!pricingSummary && item && item.custom_medicine_name) {
    return {
      purchase_cost: 0,
      suggested_unit_price: item.quoted_unit_price || "",
      margin_amount: item.quoted_unit_price ? Number(item.quoted_unit_price) : null,
      margin_percentage: null,
      source: "custom_quotation_item",
    };
  }

  if (!pricingSummary) return null;

  const sellPrice =
    item && item.quoted_unit_price !== null && item.quoted_unit_price !== undefined && item.quoted_unit_price !== ""
      ? item.quoted_unit_price
      : pricingSummary.suggested_unit_price;
  const margin = calculateMargin(sellPrice, pricingSummary.purchase_cost);

  return {
    ...pricingSummary,
    suggested_unit_price: sellPrice,
    margin_amount: margin.margin_amount,
    margin_percentage: margin.margin_percentage,
  };
};

const getSupplierSnapshot = (item) => {
  const supplierHistory = getIntakeSupplierHistory(item);
  if (supplierHistory.length) return supplierHistory[0];

  if (
    item &&
    item.selected_match &&
    item.selected_match.payload &&
    item.selected_match.payload.supplier_name
  ) {
    return item.selected_match.payload;
  }

  return null;
};

const getStoreWiseMedicationRows = (medication) => {
  if (!medication) return [];
  if (Array.isArray(medication.store_wise_medication)) return medication.store_wise_medication;
  if (Array.isArray(medication.storeWiseMedication)) return medication.storeWiseMedication;
  return [];
};

const getStockQuantityByStoreName = (rows, storeName) => {
  const normalizedStoreName = normalizeSearchValue(storeName);
  const row = (rows || []).find((stockRow) => {
    const pharmacy = stockRow && stockRow.pharmacies ? stockRow.pharmacies : {};
    return normalizeSearchValue(pharmacy.name) === normalizedStoreName;
  });

  return row && row.quantity !== null && row.quantity !== undefined ? Number(row.quantity) : 0;
};

const getStockSnapshotFromMedication = (medication) => {
  if (!medication) {
    return {
      pharmacy: 0,
      warehouse: 0,
    };
  }

  const rows = getStoreWiseMedicationRows(medication);
  const pharmacyStock = getStockQuantityByStoreName(rows, "Flash Health Pharmacy");
  const warehouseStock = getStockQuantityByStoreName(rows, "Flash WareHouse");

  return {
    pharmacy: pharmacyStock,
    warehouse: warehouseStock,
  };
};

const getStockTone = (item, stockSnapshot) => {
  const requestedQuantity = Number(item && item.requested_quantity ? item.requested_quantity : 0);
  const availableStock =
    Number(stockSnapshot && stockSnapshot.pharmacy ? stockSnapshot.pharmacy : 0) +
    Number(stockSnapshot && stockSnapshot.warehouse ? stockSnapshot.warehouse : 0);

  if (availableStock <= 0) return "danger";
  if (requestedQuantity && availableStock < requestedQuantity) return "warning";
  return "success";
};

const formatStockQuantity = (value) => {
  const numericValue = Number(value || 0);
  if (Number.isNaN(numericValue)) return "0";
  return numericValue.toLocaleString();
};

const RfqIntake = () => {
  const tokenData = JSON.parse(localStorage.getItem("user") || "{}");
  const token = tokenData.token;
  const storeId = tokenData.store_id || 1;

  const [loading, setLoading] = useState(false);
  const [generatingMatches, setGeneratingMatches] = useState(false);
  const [importingPdf, setImportingPdf] = useState(false);
  const [loadingMedicationCatalogue, setLoadingMedicationCatalogue] = useState(false);
  const [medicationCatalogueLoaded, setMedicationCatalogueLoaded] = useState(false);
  const [b2bClient, setB2bClient] = useState(null);
  const [b2bOptions, setB2bOptions] = useState([]);
  const [b2bCachedOptions, setB2bCachedOptions] = useState([]);
  const [b2bSearchLoading, setB2bSearchLoading] = useState(false);
  const [rawText, setRawText] = useState("");
  const [items, setItems] = useState([{ ...emptyItem }]);
  const [requests, setRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [message, setMessage] = useState("");
  const [deletingRequestId, setDeletingRequestId] = useState(null);
  const [b2bClientError, setB2bClientError] = useState("");
  const [medicationCatalogueOptions, setMedicationCatalogueOptions] = useState([]);
  const [medicationOptionsByItem, setMedicationOptionsByItem] = useState({});
  const [resolvingLineIndex, setResolvingLineIndex] = useState(null);
  const [clientContextLoading, setClientContextLoading] = useState(false);
  const [clientOrders, setClientOrders] = useState([]);
  const [clientRfqs, setClientRfqs] = useState([]);
  const [selectedClientOrder, setSelectedClientOrder] = useState(null);
  const [captureCollapsed, setCaptureCollapsed] = useState(false);
  const [generatingQuotation, setGeneratingQuotation] = useState(false);
  const [showQuotationNameModal, setShowQuotationNameModal] = useState(false);
  const [quotationClientName, setQuotationClientName] = useState("");
  const [quotationRep, setQuotationRep] = useState(null);
  const [quotationRepOptions, setQuotationRepOptions] = useState([]);
  const [quotationRepLoading, setQuotationRepLoading] = useState(false);
  const [stockByMedicationId, setStockByMedicationId] = useState({});
  const [recentRfqSearch, setRecentRfqSearch] = useState("");
  const [documentImportJourney, setDocumentImportJourney] = useState(null);
  const [editingRfqId, setEditingRfqId] = useState(null);
  const b2bSearchTimeoutRef = useRef(null);
  const b2bSearchRequestIdRef = useRef(0);
  const saveRfqInFlightRef = useRef(false);

  useEffect(() => {
    fetchRequests();
    loadMedicationCatalogue();

    return () => {
      if (b2bSearchTimeoutRef.current) {
        clearTimeout(b2bSearchTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (showRequestModal) {
      loadMedicationCatalogue();
    }
  }, [showRequestModal]);

  useEffect(() => {
    if (!b2bClient) {
      setClientOrders([]);
      setClientRfqs([]);
      return;
    }

    fetchClientContext(b2bClient);
  }, [b2bClient]);

  const fetchRequests = async () => {
    try {
      const res = await getRfqRequests(token, 1, {
        records_per_page: RECENT_RFQ_RESULT_LIMIT,
      });
      setRequests(res.data && res.data.data ? res.data.data : []);
    } catch (error) {
      console.error("Failed to fetch RFQ requests", error);
    }
  };

  const fetchClientContext = async (client) => {
    if (!client) {
      setClientOrders([]);
      setClientRfqs([]);
      return;
    }

    setClientContextLoading(true);
    try {
      const clientB2bId = getClientB2bId(client);
      const [ordersRes, rfqsRes] = await Promise.all([
        getRfqClientOrderHistory(token, getClientHistoryName(client)),
        getRfqRequests(token, 1, {
          b2b_id: clientB2bId,
          records_per_page: RECENT_RFQ_RESULT_LIMIT,
        }),
      ]);

      setClientOrders(
        ordersRes && ordersRes.data && Array.isArray(ordersRes.data.data)
          ? ordersRes.data.data.slice(0, 6)
          : [],
      );
      setClientRfqs(
        rfqsRes && rfqsRes.data && Array.isArray(rfqsRes.data.data)
          ? rfqsRes.data.data.slice(0, 6)
          : [],
      );
    } catch (error) {
      console.error("Failed to fetch client context", error);
      setClientOrders([]);
      setClientRfqs([]);
    } finally {
      setClientContextLoading(false);
    }
  };

  const fetchB2bClients = async (search, shouldUpdateVisibleOptions = true) => {
    try {
      const res = await searchRfqB2bClients(token, search);
      const leads =
        res && res.data && Array.isArray(res.data.data) ? res.data.data : [];

      const clients = uniqueB2bOptions(leads.map(makeB2bClientOption)).slice(
        0,
        B2B_CLIENT_RESULT_LIMIT,
      );

      setB2bCachedOptions((currentOptions) =>
        uniqueB2bOptions([...clients, ...currentOptions]),
      );
      if (shouldUpdateVisibleOptions) {
        setB2bOptions(clients);
      }
      return clients;
    } catch (error) {
      console.error("Failed to fetch B2B clients", error);
      return [];
    }
  };

  const handleB2bSearchInput = (value) => {
    const searchValue = safeText(value).trim();

    if (b2bSearchTimeoutRef.current) {
      clearTimeout(b2bSearchTimeoutRef.current);
    }

    if (!searchValue) {
      b2bSearchRequestIdRef.current += 1;
      setB2bSearchLoading(false);
      setB2bOptions(b2bCachedOptions.slice(0, B2B_CLIENT_RESULT_LIMIT));
      return value;
    }

    const localMatches = filterB2bOptions(b2bCachedOptions, searchValue);
    if (localMatches.length) {
      b2bSearchRequestIdRef.current += 1;
      setB2bSearchLoading(false);
      setB2bOptions(localMatches);
      return value;
    }

    if (searchValue.length < B2B_CLIENT_SEARCH_MIN_LENGTH) {
      b2bSearchRequestIdRef.current += 1;
      setB2bSearchLoading(false);
      setB2bOptions([]);
      return value;
    }

    const requestId = b2bSearchRequestIdRef.current + 1;
    b2bSearchRequestIdRef.current = requestId;
    setB2bSearchLoading(true);
    setB2bOptions([]);

    b2bSearchTimeoutRef.current = setTimeout(async () => {
      const remoteMatches = await fetchB2bClients(searchValue, false);
      if (b2bSearchRequestIdRef.current !== requestId) return;

      setB2bOptions(remoteMatches);
      setB2bSearchLoading(false);
    }, B2B_CLIENT_SEARCH_DEBOUNCE_MS);

    return value;
  };

  const fetchQuotationReps = async (search = "") => {
    setQuotationRepLoading(true);
    try {
      const res = await searchRfqQuotationReps(token, search);
      const users =
        res && Array.isArray(res.data)
          ? res.data
          : res && res.data && Array.isArray(res.data.data)
            ? res.data.data
            : [];
      const options = users
        .filter((user) => String(user && user.is_sales_rep) === "1" || user.is_sales_rep === true)
        .map(makeQuotationRepOption)
        .filter(Boolean);

      setQuotationRepOptions(options);

      if (!quotationRep && options.length) {
        setQuotationRep(options[0]);
      }

      return options;
    } catch (error) {
      console.error("Failed to fetch quotation reps", error);
      setQuotationRepOptions([]);
      return [];
    } finally {
      setQuotationRepLoading(false);
    }
  };

  const handleQuotationRepSearchInput = (value) => {
    fetchQuotationReps(value);
    return value;
  };

  const hydrateMedicationOptions = (request) => {
    const optionsMap = {};

    getRequestItems(request).forEach((item) => {
      const options = [];

      if (item.medication) {
        options.push(makeMedicationOption(item.medication));
      }

      if (item.selected_match && item.selected_match.medication) {
        options.push(makeMedicationOption(item.selected_match.medication));
      }

      (item.matches || []).forEach((match) => {
        if (match.medication) {
          options.push(makeMedicationOption(match.medication));
        }
      });

      optionsMap[item.id] = uniqueMedicationOptions(options);
    });

    setMedicationOptionsByItem(optionsMap);
  };

  const loadMedicationCatalogue = async () => {
    if (loadingMedicationCatalogue || medicationCatalogueLoaded) {
      return;
    }

    setLoadingMedicationCatalogue(true);
    try {
      const res = await getRfqMedicationCatalogue(token, storeId);
      const medications = getApiList(res);
      setMedicationCatalogueOptions(
        uniqueMedicationOptions(medications.map(makeMedicationOption)),
      );
      setMedicationCatalogueLoaded(true);
    } catch (error) {
      console.error("Failed to load medication catalogue", error);
    } finally {
      setLoadingMedicationCatalogue(false);
    }
  };

  const loadMedicationStock = async (medicationId) => {
    if (!medicationId || stockByMedicationId[medicationId]) {
      return;
    }

    try {
      const res = await getRfqMedicationStock(token, medicationId, storeId);
      const medication = res && res.data ? res.data : res;
      setStockByMedicationId((currentStock) => ({
        ...currentStock,
        [medicationId]: getStockSnapshotFromMedication(medication),
      }));
    } catch (error) {
      console.error("Failed to load RFQ medication stock", error);
    }
  };

  const hydrateStockForItems = (itemsToHydrate) => {
    const medicationIds = uniqueMedicationOptions(
      (itemsToHydrate || [])
        .filter((item) => item && item.medication_id)
        .map((item) => ({ value: item.medication_id })),
    ).map((option) => option.value);

    medicationIds.forEach((medicationId) => {
      loadMedicationStock(medicationId);
    });
  };

  const loadRequest = async (requestId, { openModal = false } = {}) => {
    setLoading(true);
    try {
      const res = await getRfqRequest(token, requestId);
      setSelectedRequest(res.data);
      hydrateMedicationOptions(res.data);

      if (openModal) {
        setShowRequestModal(true);
      }
    } catch (error) {
      setMessage("Could not load the RFQ details.");
    } finally {
      setLoading(false);
    }
  };

  const getSuggestedSellPrice = (medication) =>
    medication && medication.pricing_summary
      ? medication.pricing_summary.suggested_unit_price
      : medication
        ? medication.price || medication.unit_price || medication.selling_price || ""
        : "";

  const enrichItemWithMedication = (item, medication, options = {}) => ({
    ...item,
    medication_id: medication ? medication.id : null,
    medication: medication || null,
    customer_opportunity: medication ? medication.customer_opportunity : null,
    pricing_summary: medication ? medication.pricing_summary : null,
    supplier_price_history: medication ? medication.supplier_price_history || [] : [],
    stock_snapshot: medication ? getStockSnapshotFromMedication(medication) : null,
    quoted_unit_price: options.resetQuotedPrice
      ? getSuggestedSellPrice(medication)
      : item.quoted_unit_price || getSuggestedSellPrice(medication),
    status: medication ? "medicine_selected" : item.status,
  });

  const findMedicationForLine = async (query) => {
    if (!query || !b2bClient) return null;

    const res = await searchRfqMedications(
      token,
      query,
      getClientB2bId(b2bClient),
      getClientLeadId(b2bClient),
    );
    const medications = getApiList(res);
    return getBestMedicationMatch(medications, query);
  };

  const rememberClientLineMedication = async (requestedName, medication) => {
    if (!b2bClient || !requestedName || !medication || !medication.id) return;

    try {
      await learnRfqLineMapping(token, {
        b2b_id: getClientB2bId(b2bClient),
        lead_id: getClientLeadId(b2bClient),
        requested_name: requestedName,
        medication_id: medication.id,
        metadata: {
          source: "rfq_intake_ui",
        },
      });
    } catch (error) {
      console.error("Could not save RFQ line learning", error);
    }
  };

  const findLearnedClientMedication = (query) => {
    const normalizedQuery = normalizeSearchValue(query);
    if (!normalizedQuery) return null;

    for (let requestIndex = 0; requestIndex < clientRfqs.length; requestIndex += 1) {
      const requestItems = getRequestItems(clientRfqs[requestIndex]);
      for (let itemIndex = 0; itemIndex < requestItems.length; itemIndex += 1) {
        const requestItem = requestItems[itemIndex];
        if (
          normalizeSearchValue(requestItem.requested_name) === normalizedQuery &&
          requestItem.medication
        ) {
          return {
            ...requestItem.medication,
            customer_opportunity: requestItem.customer_opportunity || null,
            pricing_summary: requestItem.pricing_summary || null,
            supplier_price_history: requestItem.supplier_price_history || [],
          };
        }
      }
    }

    return null;
  };

  const enrichParsedItems = async (parsedItems) => {
    if (!parsedItems.length) return parsedItems;

    try {
      const res = await enrichRfqLines(
        token,
        parsedItems.map((item) => ({ requested_name: item.requested_name })),
        getClientB2bId(b2bClient),
        getClientLeadId(b2bClient),
      );
      const enrichedLines = getApiList(res);

      return parsedItems.map((item, index) => {
        const enrichedLine = enrichedLines[index];
        const medication = enrichedLine ? enrichedLine.medication : null;
        const learnedMedication = medication || findLearnedClientMedication(item.requested_name);
        return learnedMedication ? enrichItemWithMedication(item, learnedMedication) : item;
      });
    } catch (error) {
      console.error("Batch RFQ enrichment failed, falling back to per-line lookup", error);
      return Promise.all(
        parsedItems.map(async (item) => {
          try {
            const medication =
              findLearnedClientMedication(item.requested_name) ||
              (await findMedicationForLine(item.requested_name));
            return medication ? enrichItemWithMedication(item, medication) : item;
          } catch (lineError) {
            console.error("RFQ line enrichment failed", lineError);
            return item;
          }
        }),
      );
    }
  };

  const parseLines = async () => {
    if (!b2bClient) {
      setB2bClientError("Select a B2B client before extracting RFQ lines.");
      setMessage("Select a B2B client before extracting RFQ lines.");
      return;
    }

    if (!rawText.trim()) {
      setMessage("Paste an email body, WhatsApp message, or item list first.");
      return;
    }

    setLoading(true);
    try {
      const res = await parseRfqLines(token, rawText);
      const parsedItems = (res.data || []).map((item) => ({
        requested_name: item.requested_name,
        requested_quantity: item.requested_quantity || "",
        requested_unit: item.requested_unit || "",
        requested_description: "",
        medication_id: null,
        medication: null,
        customer_opportunity: null,
        pricing_summary: null,
        supplier_price_history: [],
        custom_medicine_name: "",
        status: item.status,
        confidence: item.confidence,
      }));

      const enrichedItems = await enrichParsedItems(parsedItems);

      setItems(enrichedItems.length ? enrichedItems : [{ ...emptyItem }]);
      hydrateStockForItems(enrichedItems);
      setCaptureCollapsed(true);
      setMessage(
        `${parsedItems.length} line(s) extracted. Medicine suggestions and win/loss report are ready for review.`,
      );
    } catch (error) {
      setMessage("Could not extract lines from this text.");
    } finally {
      setLoading(false);
    }
  };

  const handlePdfImport = async (event) => {
    const file = event.target.files && event.target.files[0] ? event.target.files[0] : null;
    event.target.value = "";

    if (!file) return;

    if (!b2bClient) {
      setB2bClientError("Select a B2B client before importing the document.");
      setMessage("Select a B2B client before importing the document.");
      return;
    }

    setImportingPdf(true);
    setLoading(true);
    setDocumentImportJourney({
      step: "reading",
      title: "Reading your document",
      detail: "We are extracting item names, quantities and UOM first. If the scan is unclear, we will ask AI to read it automatically.",
    });
    setMessage("Reading the uploaded document. For difficult scans, AI fallback will run automatically.");

    const aiFallbackNotice = window.setTimeout(() => {
      setDocumentImportJourney({
        step: "ai",
        title: "Checking harder scan",
        detail: "This is taking longer than usual. For difficult scans, the backend can use AI fallback to read the RFQ table more carefully.",
      });
      setMessage("Still reading this document. If local OCR is not confident, the AI fallback can take over.");
    }, 3500);

    try {
      let res;

      try {
        res = await importRfqDocument(
          token,
          file,
          getClientB2bId(b2bClient),
          getClientLeadId(b2bClient),
        );
      } catch (error) {
        if (!isMissingOcrToolError(error)) {
          throw error;
        }

        setDocumentImportJourney({
          step: "ai",
          title: "OCR unavailable, using AI",
          detail: "This server does not have local OCR installed, so we are sending the document directly for AI reading.",
        });
        setMessage("OCR is not installed on this server. Asking AI to read the document instead...");

        res = await importRfqDocument(
          token,
          file,
          getClientB2bId(b2bClient),
          getClientLeadId(b2bClient),
          { forceAi: true },
        );
      }

      let { importData } = getImportedItemsFromResponse(res);

      if (shouldRetryDocumentImportWithAi(importData)) {
        setDocumentImportJourney({
          step: "ai",
          title: "Asking AI to read this scan",
          detail: "The first OCR pass looked unreliable, so we are sending the document for AI reading before filling the table.",
        });
        setMessage("The first OCR result looked weak. Asking AI to read the document now...");

        const aiRes = await importRfqDocument(
          token,
          file,
          getClientB2bId(b2bClient),
          getClientLeadId(b2bClient),
          { forceAi: true },
        );
        importData = getImportedItemsFromResponse(aiRes).importData;
      }

      if (shouldBlockWeakDocumentImport(importData)) {
        setDocumentImportJourney({
          step: "failed",
          title: "AI reading is needed",
          detail: "Local OCR returned weak rows for this scan. The API needs AI fallback enabled before we can safely fill this table.",
        });
        setMessage("This scan needs AI reading. Local OCR is not accurate enough for this document.");
        return;
      }

      const parsedPdfItems = (importData.items || []).map((item) => ({
        requested_name: item.requested_name,
        requested_quantity: item.requested_quantity || "",
        requested_unit: item.requested_unit || "",
        requested_description: "",
        medication_id: null,
        medication: null,
        customer_opportunity: null,
        pricing_summary: null,
        supplier_price_history: [],
        custom_medicine_name: "",
        status: item.status,
        confidence: item.confidence,
      }));

      if (!parsedPdfItems.length) {
        setDocumentImportJourney({
          step: "failed",
          title: "No rows found",
          detail: "We could not detect medicine rows from this file. Please paste the item list or try another file.",
        });
        setMessage("Could not find medicine rows in this document. Please paste the item list or try another file.");
        return;
      }

      const cleanRawText = parsedPdfItems
        .map((item) =>
          [item.requested_name, item.requested_quantity, item.requested_unit]
            .filter(Boolean)
            .join(" "),
        )
        .join("\n");

      setRawText(cleanRawText);
      setItems(parsedPdfItems);
      setCaptureCollapsed(true);
      setDocumentImportJourney({
        step: "matching",
        title: "Rows found",
        detail: `${parsedPdfItems.length} line(s) were imported using ${getDocumentImportMethodLabel(importData.extraction_method)}. Now we are matching medicines and client history.`,
      });
      setMessage(
        `${parsedPdfItems.length} line(s) imported using ${getDocumentImportMethodLabel(importData.extraction_method)}. Checking medicine suggestions now...`,
      );

      const enrichedItems = await enrichParsedItems(parsedPdfItems);
      setItems(enrichedItems);
      hydrateStockForItems(enrichedItems);
      setDocumentImportJourney({
        step: "done",
        title: isAiDocumentImport(importData.extraction_method)
          ? "AI extraction complete"
          : "Document extraction complete",
        detail: isAiDocumentImport(importData.extraction_method)
          ? "AI helped read this file. Please review low-confidence rows before saving the RFQ."
          : "The document was readable without AI. Please review low-confidence rows before saving the RFQ.",
      });
      setMessage(
        `${parsedPdfItems.length} document line(s) imported via ${getDocumentImportMethodLabel(importData.extraction_method)}.`,
      );
    } catch (error) {
      console.error("RFQ document import failed", error);
      setDocumentImportJourney({
        step: "failed",
        title: "Import needs review",
        detail: "We could not read this document. Please paste the item list or try another clearer image/PDF.",
      });
      setMessage("Could not import this document. Please paste the item list or try another file.");
    } finally {
      window.clearTimeout(aiFallbackNotice);
      setImportingPdf(false);
      setLoading(false);
    }
  };

  const updateItem = (index, field, value) => {
    setItems(
      items.map((item, itemIndex) =>
        itemIndex === index ? { ...item, [field]: value } : item,
      ),
    );
  };

  const handleIntakeMedicationChange = async (index, value) => {
    const currentItem = items[index] || {};

    if (!value) {
      setItems(
        items.map((item, itemIndex) =>
          itemIndex === index
            ? {
              ...item,
              medication_id: null,
              medication: null,
              customer_opportunity: null,
              pricing_summary: null,
              supplier_price_history: [],
              stock_snapshot: null,
              quoted_unit_price: "",
              custom_medicine_name: "",
              status: "manual",
            }
            : item,
        ),
      );
      return;
    }

    if (value.isCustomMedicine || value.__isNew__) {
      const customName = safeText(value.customName || value.label || value.value).trim();
      setItems(
        items.map((item, itemIndex) =>
          itemIndex === index
            ? {
                ...item,
                medication_id: null,
                medication: null,
                customer_opportunity: null,
                pricing_summary: null,
                supplier_price_history: [],
                stock_snapshot: null,
                custom_medicine_name: customName,
                quoted_unit_price: item.quoted_unit_price || "",
                status: "custom_quotation_item",
              }
            : item,
        ),
      );
      setCaptureCollapsed(true);
      setMessage("Custom medicine name added for this quotation. It will not be added to the medicine master.");
      return;
    }

    if (value.payload) {
      rememberClientLineMedication(currentItem.requested_name, value.payload);
    }

    setItems(
      items.map((item, itemIndex) =>
        itemIndex === index
          ? {
              ...item,
              medication_id: value.value,
              medication: value.payload || null,
              customer_opportunity: null,
              pricing_summary: null,
              supplier_price_history: [],
              stock_snapshot: value.payload ? getStockSnapshotFromMedication(value.payload) : null,
              quoted_unit_price: value.payload ? getSuggestedSellPrice(value.payload) : "",
              custom_medicine_name: "",
              status: "medicine_selected",
            }
          : item,
      ),
    );
    loadMedicationStock(value.value);
    setCaptureCollapsed(true);

    if (!b2bClient) return;

    setResolvingLineIndex(index);
    try {
      const res = await searchRfqMedications(
        token,
        value.label,
        getClientB2bId(b2bClient),
        getClientLeadId(b2bClient),
      );
      const medications = getApiList(res);
      const selectedMedication =
        medications.find((medication) => medication.id === value.value) || value.payload;

      if (selectedMedication) {
        rememberClientLineMedication(currentItem.requested_name, selectedMedication);
      }

      setItems((currentItems) =>
        currentItems.map((item, itemIndex) =>
          itemIndex === index && selectedMedication
            ? {
                ...enrichItemWithMedication(item, selectedMedication, { resetQuotedPrice: true }),
                custom_medicine_name: "",
              }
            : item,
        ),
      );
    } catch (error) {
      setMessage("Could not load win/loss details for the selected medicine.");
    } finally {
      setResolvingLineIndex(null);
    }
  };

  const updateSelectedRequestItem = (itemId, patch) => {
    setSelectedRequest((currentRequest) => {
      if (!currentRequest) return currentRequest;

      return {
        ...currentRequest,
        items: getRequestItems(currentRequest).map((item) =>
          item.id === itemId ? { ...item, ...patch } : item,
        ),
      };
    });
  };

  const addItem = () => {
    setItems([...items, { ...emptyItem }]);
  };

  const removeItem = (index) => {
    if (items.length === 1) {
      setItems([{ ...emptyItem }]);
      return;
    }
    setItems(items.filter((item, itemIndex) => itemIndex !== index));
  };

  const syncQuotedPrices = async (request, cleanItems) => {
    if (!request || !Array.isArray(request.items)) return request;

    let latestRequest = request;
    for (let index = 0; index < cleanItems.length; index += 1) {
      const cleanItem = cleanItems[index];
      const savedItem = request.items[index];

      if (
        savedItem &&
        savedItem.id &&
        cleanItem.medication_id &&
        cleanItem.quoted_unit_price !== null &&
        cleanItem.quoted_unit_price !== undefined &&
        cleanItem.quoted_unit_price !== ""
      ) {
        try {
          const res = await assignRfqMedication(token, savedItem.id, {
            medication_id: cleanItem.medication_id,
            quoted_unit_price: cleanItem.quoted_unit_price,
          });
          latestRequest = res.data || latestRequest;
        } catch (error) {
          console.error("Failed to sync RFQ quoted price", error);
        }
      }
    }

    return latestRequest;
  };

  const saveRfq = async ({ silent = false, quotationName = "", selectedRep = null } = {}) => {
    if (saveRfqInFlightRef.current) {
      if (!silent) {
        setMessage("RFQ save is already in progress.");
      }
      return null;
    }

    if (!b2bClient) {
      setB2bClientError("Select a B2B client before saving the RFQ.");
      setMessage("Select a B2B client before saving the RFQ.");
      return null;
    }

    if (!getClientB2bId(b2bClient)) {
      setB2bClientError("Select a client that has a linked B2B organization.");
      setMessage("This client does not have a linked B2B organization yet.");
      return null;
    }

    const cleanItems = items.filter((item) => safeText(item.requested_name).trim() !== "");
    if (!cleanItems.length) {
      setMessage("Add at least one requested item.");
      return null;
    }

    setGeneratingMatches(true);
    setLoading(true);
    saveRfqInFlightRef.current = true;
    try {
      const preparedForName = safeText(quotationName).trim() || getClientQuotationName(b2bClient);
      const quotationRepMetadata = selectedRep
        ? {
            id: selectedRep.value || null,
            name: selectedRep.name || selectedRep.label || "",
            designation: selectedRep.designation || "Sales Executive",
            mobile: selectedRep.mobile || "",
            email: selectedRep.email || "",
          }
        : null;

      const savePayload = {
        b2b_id: getClientB2bId(b2bClient),
        source_type: "manual_text",
        notes: "",
        raw_text: rawText,
        metadata: {
          selected_lead_id: getClientLeadId(b2bClient),
          selected_lead_label: preparedForName,
          selected_contact_label: getClientDisplayName(b2bClient),
          selected_b2b_label: getClientHistoryName(b2bClient),
          quotation_rep: quotationRepMetadata,
        },
        items: cleanItems.map((item) => ({
          requested_name:
            !item.medication_id && item.custom_medicine_name
              ? item.custom_medicine_name
              : item.requested_name,
          requested_quantity: item.requested_quantity || null,
          requested_unit: item.requested_unit || null,
          requested_description:
            !item.medication_id && item.custom_medicine_name
              ? item.requested_name
              : item.requested_description || null,
          medication_id: item.medication_id || null,
          quoted_unit_price: item.quoted_unit_price || null,
          metadata: {
            custom_quotation_item: !item.medication_id && item.custom_medicine_name
              ? item.custom_medicine_name
              : null,
            original_requested_name: item.requested_name,
          },
        })),
      };

      const res = editingRfqId
        ? await updateRfqRequest(token, editingRfqId, savePayload)
        : await createRfqRequest(token, savePayload);

      const createdRequest = res && res.data ? res.data : null;

      if (!createdRequest || !createdRequest.id) {
        throw new Error(res && res.message ? res.message : "RFQ was not saved.");
      }

      const savedRequest = await syncQuotedPrices(createdRequest, cleanItems);

      setSelectedRequest(savedRequest);
      setEditingRfqId(savedRequest.id);
      hydrateMedicationOptions(savedRequest);
      setShowRequestModal(false);
      if (!silent) {
        setMessage(editingRfqId ? `RFQ #${savedRequest.id} updated.` : "RFQ saved.");
      }
      fetchRequests();
      return savedRequest;
    } catch (error) {
      setMessage(getApiErrorMessage(error, "Could not save RFQ. Please check the entered lines."));
      return null;
    } finally {
      saveRfqInFlightRef.current = false;
      setGeneratingMatches(false);
      setLoading(false);
    }
  };

  const handleDownloadQuotation = async () => {
    if (!b2bClient) {
      setB2bClientError("Select a B2B client before downloading the quotation.");
      setMessage("Select a B2B client before downloading the quotation.");
      return;
    }

    setQuotationClientName(getClientQuotationName(b2bClient));
    fetchQuotationReps("");
    setShowQuotationNameModal(true);
  };

  const confirmDownloadQuotation = async () => {
    const preparedForName = safeText(quotationClientName).trim() || getClientQuotationName(b2bClient);
    if (!quotationRep) {
      setMessage("Select a quotation rep before downloading the PDF.");
      return;
    }

    setShowQuotationNameModal(false);
    setGeneratingQuotation(true);
    try {
      const request = await saveRfq({
        silent: true,
        quotationName: preparedForName,
        selectedRep: quotationRep,
      });
      if (!request || !request.id) {
        return;
      }

      const response = await downloadRfqQuotationPdf(token, request.id);
      const clientFilename = sanitizeFilenamePart(preparedForName);
      downloadBlob(
        response.data,
        clientFilename
          ? `quotation-${clientFilename}-rfq-${request.id}.pdf`
          : `rfq-quotation-${request.id}.pdf`,
      );
      setMessage("Quotation PDF downloaded.");
    } catch (error) {
      setMessage(getApiErrorMessage(error, "Could not generate the quotation PDF."));
    } finally {
      setGeneratingQuotation(false);
    }
  };

  const handleOpenWhatsapp = () => {
    const phoneNumber = getClientWhatsappNumber(b2bClient);

    if (!phoneNumber) {
      setMessage("No WhatsApp number found for this client.");
      return;
    }

    const messageText = `Hi ${getClientDisplayName(
      b2bClient,
    )}, please find your Flash Health quotation attached.`;
    window.open(`https://wa.me/${phoneNumber}?text=${encodeURIComponent(messageText)}`, "_blank");
  };

  const handleOpenEmail = () => {
    const email = getClientEmail(b2bClient);

    if (!email) {
      setMessage("No email address found for this client.");
      return;
    }

    const subject = "Flash Health quotation";
    const body = `Hi ${getClientDisplayName(
      b2bClient,
    )},\n\nPlease find your Flash Health quotation attached.\n\nThank you.`;
    window.open(
      `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`,
      "_blank",
    );
  };

  const openRequest = async (request) => {
    await loadRequest(request.id, { openModal: true });
  };

  const editRequestInComposer = async (request) => {
    if (!request || !request.id) return;

    setLoading(true);
    try {
      const res = await getRfqRequest(token, request.id);
      const loadedRequest = res.data || request;
      const metadata = getRequestMetadata(loadedRequest);
      const companyName =
        metadata.selected_b2b_label ||
        (loadedRequest.b2b && loadedRequest.b2b.company_name ? loadedRequest.b2b.company_name : "");
      const contactLabel =
        metadata.selected_contact_label ||
        metadata.selected_lead_label ||
        companyName ||
        (loadedRequest.b2b ? loadedRequest.b2b.company_name : "");
      const clientOption = {
        value: metadata.selected_lead_id || loadedRequest.b2b_id,
        label: contactLabel,
        b2bId:
          loadedRequest.b2b_id ||
          (loadedRequest.b2b && loadedRequest.b2b.id ? loadedRequest.b2b.id : null),
        b2bLabel: companyName || contactLabel,
        leadId: metadata.selected_lead_id || null,
        mobileNo: "",
        email: "",
      };
      const loadedItems = getRequestItems(loadedRequest).map((requestItem) => {
        const medication =
          requestItem.medication ||
          (requestItem.selected_match && requestItem.selected_match.medication
            ? requestItem.selected_match.medication
            : null);

        return {
          ...emptyItem,
          ...requestItem,
          medication_id:
            requestItem.medication_id ||
            (requestItem.selected_match ? requestItem.selected_match.medication_id : null),
          medication,
          customer_opportunity: getCustomerOpportunity(requestItem),
          pricing_summary: getPricingSummary(requestItem),
          supplier_price_history: getIntakeSupplierHistory(requestItem).length
            ? getIntakeSupplierHistory(requestItem)
            : getSupplierHistory(requestItem),
          stock_snapshot: medication ? getStockSnapshotFromMedication(medication) : null,
          quoted_unit_price:
            requestItem.quoted_unit_price ||
            (medication ? getSuggestedSellPrice(medication) : ""),
          status: medication ? "medicine_selected" : requestItem.status || "manual",
        };
      });

      setB2bClient(clientOption);
      setB2bOptions((currentOptions) =>
        uniqueB2bOptions([clientOption, ...currentOptions]).slice(0, B2B_CLIENT_RESULT_LIMIT),
      );
      setB2bCachedOptions((currentOptions) =>
        uniqueB2bOptions([clientOption, ...currentOptions]),
      );
      setRawText(loadedRequest.raw_text || "");
      setItems(loadedItems.length ? loadedItems : [{ ...emptyItem }]);
      hydrateStockForItems(loadedItems);
      setSelectedRequest(null);
      setEditingRfqId(loadedRequest.id);
      setShowRequestModal(false);
      setCaptureCollapsed(true);
      setMessage(`RFQ #${loadedRequest.id} loaded for editing. Save will update this same RFQ.`);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      setMessage("Could not load this RFQ into the editor.");
    } finally {
      setLoading(false);
    }
  };

  const deleteRequest = async (request) => {
    if (!request || !request.id) return;

    const confirmed = window.confirm(
      `Delete RFQ #${request.id}? This will remove it from the RFQ list.`,
    );

    if (!confirmed) return;

    setDeletingRequestId(request.id);
    try {
      await deleteRfqRequest(token, request.id);
      setRequests((currentRequests) =>
        currentRequests.filter((currentRequest) => currentRequest.id !== request.id),
      );
      setClientRfqs((currentRequests) =>
        currentRequests.filter((currentRequest) => currentRequest.id !== request.id),
      );
      if (selectedRequest && selectedRequest.id === request.id) {
        setSelectedRequest(null);
        setShowRequestModal(false);
      }
      setMessage(`RFQ #${request.id} deleted.`);
    } catch (error) {
      setMessage(getApiErrorMessage(error, "Could not delete this RFQ."));
    } finally {
      setDeletingRequestId(null);
    }
  };

  const openClientOrder = async (order) => {
    if (!order || !order.id) return;

    setLoading(true);
    try {
      const res = await getRfqClientOrderDetail(token, order.id);
      setSelectedClientOrder(res.data || null);
      setShowOrderModal(true);
    } catch (error) {
      setMessage("Could not load the order details for this client.");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectMatch = async (itemId, matchId) => {
    setLoading(true);
    try {
      const res = await selectRfqMatch(token, itemId, matchId);
      setSelectedRequest(res.data);
      hydrateMedicationOptions(res.data);
      fetchRequests();
      setMessage("Suggested medicine updated.");
    } catch (error) {
      setMessage("Could not select this match.");
    } finally {
      setLoading(false);
    }
  };

  const handleMedicationDropdownOpen = (item) => {
    if (!medicationCatalogueLoaded) {
      loadMedicationCatalogue();
    }

    const requestedName = safeText(item.requested_name).trim();
    if (!requestedName) return;

    setMedicationOptionsByItem((currentMap) => ({
      ...currentMap,
      [item.id]: uniqueMedicationOptions([
        ...(currentMap[item.id] || []),
        ...medicationCatalogueOptions.filter((option) =>
          safeText(option.label).toLowerCase().includes(requestedName.toLowerCase()),
        ),
      ]),
    }));
  };

  const getMedicineDropdownOptions = (itemId) =>
    uniqueMedicationOptions([
      ...(medicationOptionsByItem[itemId] || []),
      ...medicationCatalogueOptions,
    ]);

  const handleAssignMedication = async (item) => {
    const selectedOption = getSelectedMedicationOption(
      item,
      medicationOptionsByItem[item.id] || [],
    );

    if (!selectedOption) {
      setMessage("Search and select a medicine before updating this line.");
      return;
    }

    setLoading(true);
    try {
      const res = await assignRfqMedication(token, item.id, {
        medication_id: selectedOption.value,
        quoted_unit_price: item.quoted_unit_price || null,
      });
      setSelectedRequest(res.data);
      hydrateMedicationOptions(res.data);
      fetchRequests();
      setMessage("Medicine updated for this RFQ line.");
    } catch (error) {
      setMessage("Could not update the medicine for this line.");
    } finally {
      setLoading(false);
    }
  };

  const handleOutcomeSave = async (item) => {
    setLoading(true);
    try {
      const selectedOption = getSelectedMedicationOption(
        item,
        medicationOptionsByItem[item.id] || [],
      );

      const res = await updateRfqOutcome(token, item.id, {
        outcome_status: item.outcome_status || "pending_outcome",
        outcome_quantity: item.outcome_quantity || null,
        loss_reason: item.loss_reason || null,
        competitor_price: item.competitor_price || null,
        outcome_notes: item.outcome_notes || null,
        quoted_unit_price: item.quoted_unit_price || null,
        medication_id: selectedOption ? selectedOption.value : null,
      });

      setSelectedRequest(res.data);
      hydrateMedicationOptions(res.data);
      fetchRequests();
      setMessage("Outcome saved for this quote line.");
    } catch (error) {
      setMessage("Could not save the outcome for this line.");
    } finally {
      setLoading(false);
    }
  };

  const handleDraftOrder = async () => {
    if (!selectedRequest) return;

    setLoading(true);
    try {
      const res = await createRfqDraftOrder(token, selectedRequest.id);
      setSelectedRequest(res.data);
      hydrateMedicationOptions(res.data);
      setMessage("Draft order created from matched RFQ lines.");
      fetchRequests();
    } catch (error) {
      setMessage("Could not create draft order. Check unmatched or missing quantity lines.");
    } finally {
      setLoading(false);
    }
  };

  const quotationTotal = items.reduce((total, item) => {
    const quantity = parseMoneyValue(item.requested_quantity) || 1;
    return total + quantity * getRfqItemUnitPrice(item);
  }, 0);
  const recentRequestsSource = b2bClient ? clientRfqs : requests;
  const normalizedRecentRfqSearch = normalizeSearchValue(recentRfqSearch);
  const recentRequestsFiltered = !normalizedRecentRfqSearch
    ? recentRequestsSource
    : recentRequestsSource.filter((request) => {
        const requestItemsText = getRequestItems(request)
          .map((item) => `${item.requested_name || ""} ${item.medication && item.medication.title ? item.medication.title : ""}`)
          .join(" ");
        const searchableText = normalizeSearchValue(
          [
            request.id,
            request.status,
            request.draft_order_id,
            request.created_at,
            request.received_at,
            request.b2b ? request.b2b.company_name : "",
            requestItemsText,
          ].join(" "),
        );

        return searchableText.includes(normalizedRecentRfqSearch);
      });
  const recentRequestsToShow = recentRequestsFiltered.slice(0, RECENT_RFQ_RESULT_LIMIT);

  return (
    <Grid fluid>
      {loading && !importingPdf && <LoadingSpinner />}
      <Row>
        <Col md={12}>
          <h3>RFQ Intake & Quote Automation</h3>
        </Col>
      </Row>

      {message && (
        <Row>
          <Col md={12}>
            <div className={`rfq-message rfq-message--${getMessageTone(message)}`}>
              {message}
            </div>
          </Col>
        </Row>
      )}

      {documentImportJourney && (
        <Row>
          <Col md={12}>
            <div className={`rfq-import-journey rfq-import-journey--${documentImportJourney.step}`}>
              <div className="rfq-import-journey__steps">
                <span className="rfq-import-journey__step rfq-import-journey__step--done">
                  1 Upload
                </span>
                <span
                  className={
                    documentImportJourney.step === "reading"
                      ? "rfq-import-journey__step rfq-import-journey__step--active"
                      : "rfq-import-journey__step rfq-import-journey__step--done"
                  }
                >
                  2 Read document
                </span>
                <span
                  className={
                    ["ai", "matching"].includes(documentImportJourney.step)
                      ? "rfq-import-journey__step rfq-import-journey__step--active"
                      : isAiDocumentImport(documentImportJourney.title)
                        ? "rfq-import-journey__step rfq-import-journey__step--done"
                        : "rfq-import-journey__step"
                  }
                >
                  3 AI fallback
                </span>
                <span
                  className={
                    documentImportJourney.step === "done"
                      ? "rfq-import-journey__step rfq-import-journey__step--done"
                      : documentImportJourney.step === "matching"
                        ? "rfq-import-journey__step rfq-import-journey__step--active"
                        : "rfq-import-journey__step"
                  }
                >
                  4 Match medicines
                </span>
              </div>
              <div>
                <strong>{documentImportJourney.title}</strong>
                <span>{documentImportJourney.detail}</span>
              </div>
            </div>
          </Col>
        </Row>
      )}

      <Row className={captureCollapsed ? "rfq-workspace rfq-workspace--expanded" : "rfq-workspace"}>
        <Col md={captureCollapsed ? 2 : 4} className="rfq-capture-column">
          <Card
            title="Capture RFQ"
            content={
              captureCollapsed ? (
                <div className="rfq-capture-summary">
                  <div>
                    <span style={styles.blockTitle}>Client</span>
                    <div>{getClientDisplayName(b2bClient) || "-"}</div>
                  </div>
                  <div>
                    <span style={styles.blockTitle}>Total</span>
                    <div>LKR {formatMoney(quotationTotal)}</div>
                  </div>
                  <Button
                    bsStyle="default"
                    bsSize="sm"
                    block
                    onClick={() => setCaptureCollapsed(false)}
                  >
                    Edit RFQ
                  </Button>
                  <Button bsStyle="info" bsSize="sm" block onClick={parseLines}>
                    Re-extract
                  </Button>
                </div>
              ) : (
                <div>
                  <FormGroup>
                    <ControlLabel>B2B Client *</ControlLabel>
                    <div className={b2bClientError ? "rfq-client-select has-error" : "rfq-client-select"}>
                      <Select
                        value={b2bClient}
                        options={b2bOptions}
                        isLoading={b2bSearchLoading}
                        onChange={(value) => {
                          setB2bClient(value);
                          setB2bClientError("");
                          setCaptureCollapsed(false);
                        }}
                        onInputChange={handleB2bSearchInput}
                        placeholder="Search client"
                      />
                    </div>
                    {b2bClientError && <div className="rfq-field-error">{b2bClientError}</div>}
                  </FormGroup>
                  <FormGroup>
                    <ControlLabel>Paste Email Body / WhatsApp Message / Item List</ControlLabel>
                    <div style={{ marginBottom: 10 }}>
                      <label className="btn btn-default">
                        {importingPdf ? "Importing..." : "Import RFQ File"}
                        <input
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png,.docx,.xlsx,.xls,.csv,application/pdf,image/png,image/jpeg,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
                          style={{ display: "none" }}
                          onChange={handlePdfImport}
                          disabled={importingPdf || loading}
                        />
                      </label>
                      <span style={{ ...styles.subtleText, marginLeft: 10 }}>
                        Extracts only item, quantity and UOM from PDF, image, Word or Excel.
                      </span>
                    </div>
                    <FormControl
                      componentClass="textarea"
                      style={styles.pasteBox}
                      value={rawText}
                      onChange={(e) => {
                        setRawText(e.target.value);
                        setCaptureCollapsed(false);
                      }}
                      placeholder="Phase 1: paste text manually. OCR and file parsing come later."
                    />
                  </FormGroup>
                  <Button bsStyle="info" onClick={parseLines} disabled={importingPdf}>
                    {importingPdf ? "Importing..." : "Extract Lines"}
                  </Button>{" "}
                  <Button bsStyle="default" onClick={addItem}>
                    Add Manual Line
                  </Button>
                </div>
              )
            }
          />
        </Col>

        <Col md={captureCollapsed ? 10 : 8} className="rfq-review-column">
          <Card
            title="Extract & Review RFQ Lines"
            category="Correct names, quantities and units before saving."
            content={
              <div className="rfq-generate-section">
                <div className="rfq-review-toolbar">
                  <span>
                    {captureCollapsed
                      ? "Focused review mode gives medicine, price, stock and supplier more room."
                      : "Use focus mode after selecting medicines to keep each line easier to scan."}
                  </span>
                  <Button
                    bsStyle="default"
                    bsSize="xs"
                    onClick={() => setCaptureCollapsed(!captureCollapsed)}
                  >
                    {captureCollapsed ? "Show Capture Panel" : "Focus Review"}
                  </Button>
                </div>
                {(generatingMatches || generatingQuotation || importingPdf) && (
                  <div className="rfq-generate-overlay">
                    <div className="rfq-generate-loader" />
                    <strong>
                      {importingPdf
                        ? documentImportJourney && documentImportJourney.title
                          ? documentImportJourney.title
                          : "Reading document..."
                        : generatingQuotation
                          ? "Preparing quotation..."
                          : "Saving RFQ..."}
                    </strong>
                    <span>
                      {importingPdf && documentImportJourney && documentImportJourney.detail
                        ? documentImportJourney.detail
                        : "Keeping the reviewed medicines, margins, and win/loss signals."}
                    </span>
                  </div>
                )}
                <Table responsive striped className="rfq-lines-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Item</th>
                      <th>Qty</th>
                      <th>UOM</th>
                      <th>Medicine</th>
                      <th>Status</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, index) => {
                      const customerOpportunity = getCustomerOpportunity(item);
                      const hasCustomMedicine = Boolean(safeText(item.custom_medicine_name).trim());
                      const pricingSummary = getEffectivePricingSummary(item);
                      const supplierHistory = getIntakeSupplierHistory(item);
                      const supplierSnapshot = getSupplierSnapshot(item);
                      const showSupplierOptions = isOpportunityToWin(customerOpportunity);
                      const marginTone = pricingSummary
                        ? getMarginTone(
                            pricingSummary.margin_percentage,
                            pricingSummary.margin_amount,
                          )
                        : "unknown";
                      const stockSnapshot =
                        stockByMedicationId[item.medication_id] ||
                        item.stock_snapshot ||
                        getStockSnapshotFromMedication(item.medication);
                      const stockTone = getStockTone(item, stockSnapshot);
                      const intakeStatus = resolvingLineIndex === index
                        ? "checking_history"
                        : customerOpportunity && customerOpportunity.status
                          ? customerOpportunity.status
                          : hasCustomMedicine
                            ? "custom_quotation_item"
                            : item.medication_id
                            ? "medicine_selected"
                            : item.status || "manual";

                      return (
                        <React.Fragment key={index}>
                          <tr>
                            <td>{index + 1}</td>
                            <td>
                              <FormControl
                                value={item.requested_name}
                                onChange={(e) =>
                                  updateItem(index, "requested_name", e.target.value)
                                }
                                placeholder="Requested item"
                              />
                            </td>
                            <td>
                              <FormControl
                                style={styles.tableInput}
                                value={item.requested_quantity}
                                onChange={(e) =>
                                  updateItem(index, "requested_quantity", e.target.value)
                                }
                                placeholder="Qty"
                              />
                            </td>
                            <td>
                              <FormControl
                                style={styles.tableInput}
                                value={item.requested_unit}
                                onChange={(e) =>
                                  updateItem(index, "requested_unit", e.target.value)
                                }
                                placeholder="NOS"
                              />
                            </td>
                            <td>
                              <CreatableSelect
                                value={getSelectedMedicationOption(item, medicationCatalogueOptions)}
                                options={medicationCatalogueOptions}
                                searchable
                                clearable
                                promptTextCreator={(label) => `Use "${label}" as custom quote item`}
                                formatCreateLabel={(label) => `Use "${label}" as custom quote item`}
                                placeholder="Select medicine"
                                noResultsText={
                                  loadingMedicationCatalogue
                                    ? "Loading medicines..."
                                    : "No medicine found. Type the full name and press enter to use it only for this quotation."
                                }
                                onChange={(value) => handleIntakeMedicationChange(index, value)}
                              />
                              {hasCustomMedicine && (
                                <div className="rfq-custom-medicine-note">
                                  Custom quote item. This will not be saved to the medicine master.
                                </div>
                              )}
                            </td>
                            <td className="rfq-status-cell">
                              <Label className="rfq-status-label" bsStyle={statusLabel(intakeStatus)}>
                                {intakeStatus}
                              </Label>
                            </td>
                            <td className="rfq-actions-cell">
                              <Button
                                bsStyle="danger"
                                bsSize="xs"
                                onClick={() => removeItem(index)}
                              >
                                Remove
                              </Button>
                            </td>
                          </tr>
                          {(customerOpportunity || pricingSummary || hasCustomMedicine) && (
                            <tr className="rfq-intake-insight-row">
                              <td></td>
                              <td colSpan={6}>
                                <div className="rfq-intake-insight">
                                  <div className="rfq-opportunity-panel">
                                    <span className="rfq-compact-title">
                                      {isRepeatWinner(customerOpportunity) ? "Winning" : "Opportunity"}
                                    </span>
                                    {customerOpportunity ? (
                                      <div>
                                        <Label bsStyle={statusLabel(customerOpportunity.status)}>
                                          {customerOpportunity.label}
                                        </Label>
                                        <div style={{ marginTop: 6 }}>
                                          {customerOpportunity.guidance}
                                        </div>
                                        <div style={{ ...styles.subtleText, marginTop: 6 }}>
                                          Quoted before: {customerOpportunity.quote_count || 0} |
                                          Ordered before: {customerOpportunity.order_count || 0}
                                        </div>
                                        {customerOpportunity.latest_order_item && (
                                          <div style={{ ...styles.subtleText, marginTop: 4 }}>
                                            Last accepted price:{" "}
                                            {formatMoney(
                                              customerOpportunity.latest_order_item.unit_price,
                                            )}
                                          </div>
                                        )}
                                        {!isRepeatWinner(customerOpportunity) &&
                                          customerOpportunity.latest_quote_item && (
                                            <div style={{ ...styles.subtleText, marginTop: 4 }}>
                                              Last quoted price:{" "}
                                              {formatMoney(
                                                customerOpportunity.latest_quote_item.quoted_unit_price,
                                              )}
                                            </div>
                                          )}
                                      </div>
                                    ) : (
                                      <div style={styles.subtleText}>
                                        Select a medicine to check customer history.
                                      </div>
                                    )}
                                  </div>

                                  <div className="rfq-price-panel">
                                    {pricingSummary ? (
                                      <div className="rfq-price-strip">
                                        <div className={`rfq-margin-card rfq-margin-card--${marginTone}`}>
                                          <div>
                                            <span>{getMarginLabel(marginTone)}</span>
                                            <strong>
                                              {formatPercent(pricingSummary.margin_percentage)}
                                            </strong>
                                          </div>
                                          <small>
                                            Margin value: {formatMoney(pricingSummary.margin_amount)}
                                          </small>
                                        </div>
                                        <div className="rfq-price-metric">
                                          <span>Cost</span>
                                          <strong>
                                            {formatMoney(pricingSummary.purchase_cost)}
                                          </strong>
                                        </div>
                                        <FormGroup className="rfq-selling-price-field">
                                          <ControlLabel>Selling Price</ControlLabel>
                                          <FormControl
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            value={numberOrEmpty(
                                              item.quoted_unit_price ||
                                                pricingSummary.suggested_unit_price,
                                            )}
                                            onChange={(e) =>
                                              updateItem(index, "quoted_unit_price", e.target.value)
                                            }
                                            placeholder="Sell price"
                                          />
                                        </FormGroup>
                                        <div className={`rfq-stock-card rfq-stock-card--${stockTone}`}>
                                          <div>
                                            <span>Pharmacy</span>
                                            <strong>{formatStockQuantity(stockSnapshot.pharmacy)}</strong>
                                          </div>
                                          <div>
                                            <span>Warehouse</span>
                                            <strong>{formatStockQuantity(stockSnapshot.warehouse)}</strong>
                                          </div>
                                        </div>
                                      </div>
                                    ) : (
                                      <div style={styles.subtleText}>No margin data yet.</div>
                                    )}
                                  </div>

                                  <div className="rfq-supplier-panel">
                                    <span className="rfq-compact-title">Supplier</span>
                                    {supplierSnapshot ? (
                                      <div className="rfq-mini-grid">
                                        <div>
                                          {supplierSnapshot.supplier_name || "Unknown supplier"}
                                        </div>
                                        <div>
                                          Buy:{" "}
                                          {formatMoney(
                                            supplierSnapshot.purchase_cost ||
                                              supplierSnapshot.amount,
                                          )}
                                        </div>
                                        <div>
                                          Last sell:{" "}
                                          {formatMoney(
                                            supplierSnapshot.selling_price ||
                                              supplierSnapshot.pack_price,
                                          )}
                                        </div>
                                      </div>
                                    ) : (
                                      <div style={styles.subtleText}>
                                        No supplier memory found for this item.
                                      </div>
                                    )}
                                  </div>

                                  {showSupplierOptions && (
                                    <div>
                                      <span style={styles.blockTitle}>
                                        Supplier Options
                                      </span>
                                      {supplierHistory.length ? (
                                        <div className="rfq-mini-grid">
                                          {supplierHistory.slice(0, 3).map((supplierRow) => (
                                            <div key={supplierRow.purchase_order_item_id}>
                                              <strong>
                                                {supplierRow.supplier_name || "Unknown supplier"}
                                              </strong>
                                              <div>
                                                Cost: {formatMoney(supplierRow.purchase_cost)}
                                              </div>
                                              <div>
                                                Selling:{" "}
                                                {formatMoney(
                                                  supplierRow.selling_price ||
                                                    supplierRow.pack_price,
                                                )}
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      ) : (
                                        <div style={styles.subtleText}>
                                          No supplier price history found.
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </Table>
                <Row>
                  <Col md={12}>
                    <div className="rfq-total-summary">
                      <div>
                        <span>Quotation Total</span>
                        <strong>LKR {formatMoney(quotationTotal)}</strong>
                      </div>
                      <small>Based on the current selling prices.</small>
                    </div>
                  </Col>
                </Row>
                <Button
                  bsStyle="success"
                  onClick={() => saveRfq()}
                  disabled={generatingMatches || generatingQuotation}
                >
                  {generatingMatches
                    ? "Saving RFQ..."
                    : editingRfqId
                      ? "Save Changes"
                      : "Save RFQ"}
                </Button>{" "}
                <Button
                  bsStyle="primary"
                  onClick={handleDownloadQuotation}
                  disabled={generatingMatches || generatingQuotation}
                >
                  {generatingQuotation ? "Generating..." : "Download Quotation PDF"}
                </Button>{" "}
                <Button
                  bsStyle="success"
                  onClick={handleOpenWhatsapp}
                  disabled={!b2bClient || generatingMatches || generatingQuotation}
                >
                  WhatsApp
                </Button>{" "}
                <Button
                  bsStyle="info"
                  onClick={handleOpenEmail}
                  disabled={!b2bClient || generatingMatches || generatingQuotation}
                >
                  Email
                </Button>{" "}
                <Button
                  bsStyle="default"
                  onClick={addItem}
                  disabled={generatingMatches || generatingQuotation}
                >
                  Add Manual Line
                </Button>
                {b2bClient && (
                  <div className="rfq-client-context" style={{ marginTop: 20 }}>
                    <div className="rfq-client-context__header">
                      <div>
                        <div className="rfq-client-context__eyebrow">Client Memory</div>
                        <h4>{getClientDisplayName(b2bClient)}</h4>
                        <div style={styles.subtleText}>
                          Recent customer orders and RFQs to keep the intake fast.
                        </div>
                      </div>
                      {clientContextLoading && (
                        <div style={styles.pillMetric}>Loading recent history...</div>
                      )}
                    </div>

                    <Row>
                      <Col md={4}>
                        <div style={styles.contextMetric}>
                          <strong>{clientOrders.length}</strong>
                          <div>Recent Orders Found</div>
                          <div style={{ ...styles.subtleText, marginTop: 6 }}>
                            Click any order to inspect its lines and accepted prices.
                          </div>
                        </div>
                      </Col>
                      <Col md={4}>
                        <div style={styles.contextMetric}>
                          <strong>{clientRfqs.length}</strong>
                          <div>Recent RFQs Found</div>
                          <div style={{ ...styles.subtleText, marginTop: 6 }}>
                            Reopen recent RFQs without leaving this page.
                          </div>
                        </div>
                      </Col>
                      <Col md={4}>
                        <div style={styles.contextMetric}>
                          <strong>
                            {clientOrders.length && clientOrders[0].order_date
                              ? formatDate(clientOrders[0].order_date)
                              : "-"}
                          </strong>
                          <div>Last Order Date</div>
                          <div style={{ ...styles.subtleText, marginTop: 6 }}>
                            Fast signal for whether this is a repeat buying account.
                          </div>
                        </div>
                      </Col>
                    </Row>

                    <div className="rfq-client-context__grid">
                      <div className="rfq-client-memory-panel">
                        <div className="rfq-client-memory-panel__title">Recent Orders</div>
                        {!clientOrders.length && !clientContextLoading && (
                          <div style={styles.subtleText}>
                            No recent customer orders found for this B2B client.
                          </div>
                        )}
                        <div className="rfq-client-memory-list">
                          {clientOrders.map((order) => (
                            <div key={order.id} className="rfq-client-memory-card">
                              <div className="rfq-client-memory-card__row">
                                <strong>Order #{order.id}</strong>
                                <Label bsStyle={statusLabel(order.order_status_label || order.status_label || "pending")}>
                                  {order.order_status_label || order.status_label || "order"}
                                </Label>
                              </div>
                              <div className="rfq-client-memory-card__meta">
                                <span>Date: {formatDate(order.order_date || order.created_at)}</span>
                                <span>PO: {order.po_number || "-"}</span>
                              </div>
                              <div className="rfq-client-memory-card__meta">
                                <span>Basket: {formatMoney(order.basket_value || order.total_cost)}</span>
                                <span>
                                  Lines:{" "}
                                  {order.order_items_count ||
                                    order.item_count ||
                                    (order.order_items ? order.order_items.length : "-")}
                                </span>
                              </div>
                              <Button bsStyle="default" bsSize="xs" onClick={() => openClientOrder(order)}>
                                View Lines
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="rfq-client-memory-panel">
                        <div className="rfq-client-memory-panel__title">Recent RFQs</div>
                        {!clientRfqs.length && !clientContextLoading && (
                          <div style={styles.subtleText}>
                            No recent RFQs found for this B2B client.
                          </div>
                        )}
                        <div className="rfq-client-memory-list">
                          {clientRfqs.map((request) => (
                            <div key={request.id} className="rfq-client-memory-card">
                              <div className="rfq-client-memory-card__row">
                                <strong>RFQ #{request.id}</strong>
                                <Label bsStyle={statusLabel(request.status)}>{request.status}</Label>
                              </div>
                              <div className="rfq-client-memory-card__meta">
                                <span>Created: {formatDate(request.received_at || request.created_at)}</span>
                                <span>Lines: {getRequestItems(request).length}</span>
                              </div>
                              <div className="rfq-client-memory-card__meta">
                                <span>Draft Order: {request.draft_order_id || "-"}</span>
                              </div>
                              <div className="rfq-action-group">
                                <Button
                                  className="rfq-action-btn rfq-action-btn--review"
                                  bsSize="xs"
                                  onClick={() => openRequest(request)}
                                >
                                  Review
                                </Button>
                                <Button
                                  className="rfq-action-btn rfq-action-btn--edit"
                                  bsSize="xs"
                                  onClick={() => editRequestInComposer(request)}
                                >
                                  Edit
                                </Button>
                                <Button
                                  className="rfq-action-btn rfq-action-btn--delete"
                                  bsSize="xs"
                                  onClick={() => deleteRequest(request)}
                                  disabled={deletingRequestId === request.id}
                                >
                                  {deletingRequestId === request.id ? "Deleting..." : "Delete"}
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            }
          />
        </Col>
      </Row>

      <Row>
        <Col md={12}>
          <Card
            title={b2bClient ? `Recent RFQs for ${getClientDisplayName(b2bClient)}` : "Recent RFQs"}
            content={
              <div>
                <div className="rfq-recent-search">
                  <FormControl
                    value={recentRfqSearch}
                    onChange={(e) => setRecentRfqSearch(e.target.value)}
                    placeholder="Search RFQ by client, medicine, status, draft order, or RFQ ID..."
                  />
                  <span>
                    Showing {recentRequestsToShow.length} of {recentRequestsFiltered.length}
                  </span>
                </div>
                <Table responsive striped>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Created At</th>
                      <th>Client</th>
                      <th>Status</th>
	                      <th>Total Amount</th>
	                      <th>Draft Order</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentRequestsToShow.map((request) => (
                      <tr key={request.id}>
                        <td>{request.id}</td>
                        <td>{formatDateTime(request.created_at || request.received_at)}</td>
                        <td>{request.b2b ? request.b2b.company_name : "-"}</td>
                        <td>
                          <Label bsStyle={statusLabel(request.status)}>
                            {request.status}
                          </Label>
                        </td>
	                        <td>LKR {formatMoney(getRfqRequestTotal(request))}</td>
                        <td>{request.draft_order_id || "-"}</td>
                        <td>
                          <div className="rfq-action-group">
                            <Button
                              className="rfq-action-btn rfq-action-btn--review"
                              bsSize="xs"
                              onClick={() => openRequest(request)}
                            >
                              Review
                            </Button>
                            <Button
                              className="rfq-action-btn rfq-action-btn--edit"
                              bsSize="xs"
                              onClick={() => editRequestInComposer(request)}
                            >
                              Edit
                            </Button>
                            <Button
                              className="rfq-action-btn rfq-action-btn--delete"
                              bsSize="xs"
                              onClick={() => deleteRequest(request)}
                              disabled={deletingRequestId === request.id}
                            >
                              {deletingRequestId === request.id ? "Deleting..." : "Delete"}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {!recentRequestsToShow.length && (
                      <tr>
                        <td colSpan={7} style={{ textAlign: "center", color: "#667085" }}>
                          No RFQs found for this search.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            }
          />
        </Col>
      </Row>

      <Modal
        show={showRequestModal}
        onHide={() => setShowRequestModal(false)}
        dialogClassName="rfq-review-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            RFQ Review {selectedRequest ? `#${selectedRequest.id}` : ""}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedRequest && (
            <div>
              <Row>
                <Col md={3}>
                  <div style={styles.metric}>
                    <strong>{selectedRequest.b2b ? selectedRequest.b2b.company_name : "-"}</strong>
                    <div>Client</div>
                  </div>
                </Col>
                <Col md={3}>
                  <div style={styles.metric}>
                    <strong>{selectedRequest.status}</strong>
                    <div>Status</div>
                  </div>
                </Col>
                <Col md={3}>
                  <div style={styles.metric}>
                    <strong>{selectedRequest.draft_order_id || "-"}</strong>
                    <div>Draft Order</div>
                  </div>
                </Col>
                <Col md={3}>
                  <div style={styles.metric}>
                    <strong>{getRequestItems(selectedRequest).length}</strong>
                    <div>Quoted Lines</div>
                  </div>
                </Col>
              </Row>

              {getRequestItems(selectedRequest).map((item) => {
                const medicationOptions = getMedicineDropdownOptions(item.id);
                const selectedMedication = getSelectedMedicationOption(item, medicationOptions);
                const supplierHistory = getSupplierHistory(item);
                const outcomeSummary = getOutcomeSummary(item);
                const customerOpportunity = getCustomerOpportunity(item);

                return (
                  <div key={item.id} style={styles.matchCard}>
                    <Row>
                      <Col md={4}>
                        <strong>{item.requested_name}</strong>
                        <div>
                          Qty: {item.requested_quantity || "missing"} {item.requested_unit || ""}
                        </div>
                        <div style={{ marginTop: 6 }}>
                          <Label bsStyle={statusLabel(item.status)}>{item.status}</Label>{" "}
                          <Label bsStyle={statusLabel(item.outcome_status)}>
                            {item.outcome_status || "pending_outcome"}
                          </Label>
                        </div>
                        {customerOpportunity && (
                          <div style={styles.opportunityPanel}>
                            <span style={styles.blockTitle}>Customer Opportunity</span>
                            <Label bsStyle={statusLabel(customerOpportunity.status)}>
                              {customerOpportunity.label}
                            </Label>
                            <div style={{ marginTop: 8 }}>
                              {customerOpportunity.guidance}
                            </div>
                            <div style={{ ...styles.subtleText, marginTop: 8 }}>
                              Quoted before: {customerOpportunity.quote_count || 0} | Ordered before:{" "}
                              {customerOpportunity.order_count || 0}
                            </div>
                            {customerOpportunity.latest_order_item && (
                              <div style={{ ...styles.subtleText, marginTop: 4 }}>
                                Last order price: {customerOpportunity.latest_order_item.unit_price || "-"}
                              </div>
                            )}
                            {customerOpportunity.latest_quote_item && (
                              <div style={{ ...styles.subtleText, marginTop: 4 }}>
                                Last quoted price:{" "}
                                {customerOpportunity.latest_quote_item.quoted_unit_price || "-"}
                              </div>
                            )}
                          </div>
                        )}
                        <div style={{ marginTop: 12 }}>
                          <span style={styles.blockTitle}>Selected Medicine</span>
                          <Select
                            value={selectedMedication}
                            options={medicationOptions}
                            searchable
                            onOpen={() => handleMedicationDropdownOpen(item)}
                            onChange={(value) => {
                              updateSelectedRequestItem(item.id, {
                                medication_id: value ? value.value : null,
                                medication: value ? value.payload : null,
                              });
                              setMedicationOptionsByItem((currentMap) => ({
                                ...currentMap,
                                [item.id]: uniqueMedicationOptions(
                                  value ? [value, ...medicationOptions] : medicationOptions,
                                ),
                              }));
                            }}
                            placeholder="Search full medication master"
                            noResultsText={
                              loadingMedicationCatalogue
                                ? "Loading medications..."
                                : "No medicine found in the local catalogue."
                            }
                          />
                          <div style={styles.subtleText}>
                            Fast local search across the medication catalogue, just like orders.
                          </div>
                          <div style={{ marginTop: 8 }}>
                            <Button bsStyle="primary" bsSize="xs" onClick={() => handleAssignMedication(item)}>
                              Update Medicine
                            </Button>
                          </div>
                        </div>

                        <div style={styles.outcomePanel}>
                          <span style={styles.blockTitle}>Outcome Tracking</span>
                          <FormGroup>
                            <ControlLabel>Outcome</ControlLabel>
                            <Select
                              value={outcomeOptions.find(
                                (option) =>
                                  option.value === (item.outcome_status || "pending_outcome"),
                              )}
                              options={outcomeOptions}
                              onChange={(value) =>
                                updateSelectedRequestItem(item.id, {
                                  outcome_status: value ? value.value : "pending_outcome",
                                })
                              }
                            />
                          </FormGroup>
                          <Row>
                            <Col md={6}>
                              <FormGroup>
                                <ControlLabel>Quoted Price</ControlLabel>
                                <FormControl
                                  value={numberOrEmpty(item.quoted_unit_price)}
                                  onChange={(e) =>
                                    updateSelectedRequestItem(item.id, {
                                      quoted_unit_price: e.target.value,
                                    })
                                  }
                                  placeholder="Quoted"
                                />
                              </FormGroup>
                            </Col>
                            <Col md={6}>
                              <FormGroup>
                                <ControlLabel>Won Qty</ControlLabel>
                                <FormControl
                                  value={numberOrEmpty(item.outcome_quantity)}
                                  onChange={(e) =>
                                    updateSelectedRequestItem(item.id, {
                                      outcome_quantity: e.target.value,
                                    })
                                  }
                                  placeholder="Outcome qty"
                                />
                              </FormGroup>
                            </Col>
                          </Row>
                          <Row>
                            <Col md={6}>
                              <FormGroup>
                                <ControlLabel>Loss Reason</ControlLabel>
                                <Select
                                  value={lossReasonOptions.find(
                                    (option) => option.value === item.loss_reason,
                                  )}
                                  options={lossReasonOptions}
                                  onChange={(value) =>
                                    updateSelectedRequestItem(item.id, {
                                      loss_reason: value ? value.value : null,
                                    })
                                  }
                                  placeholder="Reason if lost"
                                />
                              </FormGroup>
                            </Col>
                            <Col md={6}>
                              <FormGroup>
                                <ControlLabel>Competitor Price</ControlLabel>
                                <FormControl
                                  value={numberOrEmpty(item.competitor_price)}
                                  onChange={(e) =>
                                    updateSelectedRequestItem(item.id, {
                                      competitor_price: e.target.value,
                                    })
                                  }
                                  placeholder="Competitor price"
                                />
                              </FormGroup>
                            </Col>
                          </Row>
                          <FormGroup>
                            <ControlLabel>Outcome Notes</ControlLabel>
                            <FormControl
                              componentClass="textarea"
                              value={item.outcome_notes || ""}
                              onChange={(e) =>
                                updateSelectedRequestItem(item.id, {
                                  outcome_notes: e.target.value,
                                })
                              }
                              placeholder="Any detail from the customer or procurement"
                            />
                          </FormGroup>
                          <Button bsStyle="warning" bsSize="sm" onClick={() => handleOutcomeSave(item)}>
                            Save Outcome
                          </Button>
                        </div>
                      </Col>

                      <Col md={8}>
                        <span style={styles.blockTitle}>Suggested Matches</span>
                        <div className="rfq-matches-table">
                          <Table condensed responsive>
                            <thead>
                              <tr>
                                <th>Match</th>
                                <th>PO Cost</th>
                                <th>Suggested</th>
                                <th>Margin</th>
                                <th></th>
                              </tr>
                            </thead>
                            <tbody>
                              {(item.matches || []).map((match) => (
                                <tr key={match.id}>
                                  <td>
                                    {match.match_label}
                                    {!match.medication_id && (
                                      <div style={styles.subtleText}>Search and select manually</div>
                                    )}
                                  </td>
                                  <td>{match.purchase_cost || "-"}</td>
                                  <td>{match.suggested_unit_price || "-"}</td>
                                  <td>
                                    {match.margin_percentage !== null &&
                                    match.margin_percentage !== undefined
                                      ? `${Number(match.margin_percentage).toFixed(2)}%`
                                      : "-"}
                                  </td>
                                  <td>
                                    {match.medication_id ? (
                                      <Button
                                        bsStyle={match.is_selected ? "success" : "default"}
                                        bsSize="xs"
                                        onClick={() => handleSelectMatch(item.id, match.id)}
                                      >
                                        {match.is_selected ? "Selected" : "Select"}
                                      </Button>
                                    ) : (
                                      <Label bsStyle="warning">Manual</Label>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>

                        <Row style={styles.sectionSpacer}>
                          <Col md={5}>
                            <div style={styles.outcomePanel}>
                              <span style={styles.blockTitle}>Current Pricing View</span>
                              <div>Quoted Unit Price: {item.quoted_unit_price || "-"}</div>
                              <div>
                                Margin:{" "}
                                {item.quoted_margin_percentage !== null &&
                                item.quoted_margin_percentage !== undefined
                                  ? `${Number(item.quoted_margin_percentage).toFixed(2)}%`
                                  : "-"}
                              </div>
                              <div style={styles.subtleText}>
                                Selected by history, manual review, or medicine catalogue match.
                              </div>
                            </div>
                          </Col>

                          <Col md={7}>
                            <div style={styles.outcomePanel}>
                              <span style={styles.blockTitle}>Supplier Price History</span>
                              {!supplierHistory.length && (
                                <div style={styles.subtleText}>No supplier price history yet.</div>
                              )}
                              <div className="rfq-supplier-grid">
                                {supplierHistory.map((supplierRow) => (
                                  <div
                                    key={supplierRow.purchase_order_item_id}
                                    style={styles.supplierCard}
                                  >
                                    <strong>{supplierRow.supplier_name || "Unknown supplier"}</strong>
                                    <div>Cost: {supplierRow.purchase_cost || "-"}</div>
                                    <div>
                                      Selling: {supplierRow.selling_price || supplierRow.pack_price || "-"}
                                    </div>
                                    <div>Qty: {supplierRow.quantity || "-"}</div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </Col>
                        </Row>

                        <Row style={styles.sectionSpacer}>
                          <Col md={12}>
                            <div style={styles.outcomePanel}>
                              <span style={styles.blockTitle}>Win / Loss Memory</span>
                              {outcomeSummary ? (
                                <div className="rfq-memory-grid">
                                  <div>Won: {outcomeSummary.won || 0}</div>
                                  <div>Lost: {outcomeSummary.lost || 0}</div>
                                  <div>Partial: {outcomeSummary.partial || 0}</div>
                                  <div>Pending: {outcomeSummary.pending_outcome || 0}</div>
                                  <div>Avg quoted: {outcomeSummary.avg_quoted_price || "-"}</div>
                                  <div>Avg competitor: {outcomeSummary.avg_competitor_price || "-"}</div>
                                </div>
                              ) : (
                                <div style={styles.subtleText}>No past outcome history yet.</div>
                              )}
                            </div>
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                  </div>
                );
              })}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setShowRequestModal(false)}>Close</Button>
          <Button bsStyle="success" onClick={handleDraftOrder}>
            Create Draft Order
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showOrderModal}
        onHide={() => setShowOrderModal(false)}
        dialogClassName="rfq-review-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Client Order {selectedClientOrder ? `#${selectedClientOrder.id}` : ""}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedClientOrder && (
            <div>
              <Row>
                <Col md={3}>
                  <div style={styles.metric}>
                    <strong>{selectedClientOrder.po_number || "-"}</strong>
                    <div>PO Number</div>
                  </div>
                </Col>
                <Col md={3}>
                  <div style={styles.metric}>
                    <strong>{selectedClientOrder.order_status_label || "-"}</strong>
                    <div>Status</div>
                  </div>
                </Col>
                <Col md={3}>
                  <div style={styles.metric}>
                    <strong>{formatDate(selectedClientOrder.order_date)}</strong>
                    <div>Order Date</div>
                  </div>
                </Col>
                <Col md={3}>
                  <div style={styles.metric}>
                    <strong>{formatMoney(selectedClientOrder.basket_value || selectedClientOrder.total_cost)}</strong>
                    <div>Basket Value</div>
                  </div>
                </Col>
              </Row>

              <div style={{ marginTop: 20 }}>
                <Table responsive striped>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Item</th>
                      <th>Qty</th>
                      <th>Unit Price</th>
                      <th>Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(selectedClientOrder.orderItems || []).map((orderItem, index) => (
                      <tr key={orderItem.id || index}>
                        <td>{index + 1}</td>
                        <td>
                          {safeText(
                            orderItem.description ||
                              (orderItem.medication && orderItem.medication.title) ||
                              (orderItem.medicine && orderItem.medicine.title) ||
                              "Unnamed item",
                          )}
                        </td>
                        <td>{orderItem.quantity || "-"}</td>
                        <td>{formatMoney(orderItem.unit_price)}</td>
                        <td>{formatMoney(orderItem.subtotal)}</td>
                      </tr>
                    ))}
                    {!(selectedClientOrder.orderItems || []).length && (
                      <tr>
                        <td colSpan={5} style={{ textAlign: "center", color: "#667085" }}>
                          No order lines returned for this order.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setShowOrderModal(false)}>Close</Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showQuotationNameModal}
        onHide={() => setShowQuotationNameModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Quotation Name</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <FormGroup>
            <ControlLabel>Prepared For</ControlLabel>
            <FormControl
              type="text"
              value={quotationClientName}
              onChange={(e) => setQuotationClientName(e.target.value)}
              placeholder="Enter the name to print on the quotation"
            />
            <div style={{ ...styles.subtleText, marginTop: 8 }}>
              This is the name that will be printed in the quotation PDF.
            </div>
          </FormGroup>
          <FormGroup>
            <ControlLabel>Quotation Rep</ControlLabel>
            <Select
              value={quotationRep}
              options={quotationRepOptions}
              onChange={(value) => setQuotationRep(value)}
              onInputChange={handleQuotationRepSearchInput}
              placeholder="Search and select rep"
              noResultsText={quotationRepLoading ? "Loading reps..." : "No reps found."}
            />
            {quotationRep && (
              <div style={{ ...styles.subtleText, marginTop: 8 }}>
                {quotationRep.mobile || "No mobile"}{" "}
                {quotationRep.email ? `| ${quotationRep.email}` : ""}
              </div>
            )}
          </FormGroup>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setShowQuotationNameModal(false)}>Cancel</Button>
          <Button
            bsStyle="primary"
            onClick={confirmDownloadQuotation}
            disabled={generatingQuotation || !quotationRep}
          >
            {generatingQuotation ? "Generating..." : "Confirm & Download"}
          </Button>
        </Modal.Footer>
      </Modal>
    </Grid>
  );
};

export default RfqIntake;
